import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  UserIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ChevronRightIcon,
  ClockIcon,
  CheckBadgeIcon,
  MapPinIcon,
  ShieldCheckIcon as ShieldCheckIconOutline,
  HeartIcon as HeartIconOutline,
  SparklesIcon as SparklesIconOutline,
  UserCircleIcon as UserCircleIconOutline,
  HandRaisedIcon as HandRaisedIconOutline,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  SparklesIcon,
  HeartIcon,
  ShieldCheckIcon,
  StarIcon,
  UserCircleIcon,
  HandRaisedIcon,
} from "@heroicons/react/24/solid";

import logo from "./img/logo.svg"
import cicoaIcon from "./img/cicoa.png";
import mhmIcon from "./img/mhm.png";
import unitedIcon from "./img/united.png";
import massIcon from "./img/mass.png";
import personalCareImg from "./img/personalcare.png";
import skilledNursingImg from "./img/skillednursing.png";
import companionshipImg from "./img/companionship.png";


import './landing.scss'
import axios from "axios";
// import './landing.module.scss'

function QuoteIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
    </svg>
  );
}

const userTypes = [
  {
    id: "individual",
    title: "Individual or Family Member",
    description: "Seeking care for yourself or a loved one",
    icon: UserIcon,
  },
  {
    id: "caregiver",
    title: "Caregiver",
    description: "Providing professional care services",
    icon: UserGroupIcon,
  },
  {
    id: "partner",
    title: "Partner Organization",
    description: "Healthcare facilities and organizations",
    icon: BuildingOfficeIcon,
  },
];

const services = [
  {
    title: "Personal Care",
    description: "Assistance with daily activities, hygiene, and mobility",
    icon: UserCircleIconOutline,
    features: [
      "24/7 Availability",
      "Personalized Care Plans",
      "Trained Professionals",
    ],
    backgroundImage: personalCareImg,
  },
  {
    title: "Skilled Nursing",
    description: "Professional medical care and health monitoring",
    icon: ShieldCheckIconOutline,
    features: ["Licensed Nurses", "Medical Care", "Health Monitoring"],
    backgroundImage: skilledNursingImg,
  },
  {
    title: "Companionship",
    description: "Social interaction and emotional support",
    icon: HandRaisedIconOutline,
    features: ["Regular Activities", "Emotional Support", "Social Engagement"],
    backgroundImage: companionshipImg,
  },
];

const stats = [
  {
    label: "Active Caregivers",
    value: "500+",
    description: "Qualified and vetted professionals ready to help",
    icon: UserGroupIcon,
    image: "hero.jpg",
  },
  {
    label: "Satisfied Clients",
    value: "2,000+",
    description: "Families who found their perfect care match",
    icon: HeartIcon,
    image: "/stats/families.jpg",
  },
  {
    label: "Care Hours",
    value: "100k+",
    description: "Hours of quality care provided to families",
    icon: ClockIcon,
    image: "/stats/care-hours.jpg",
  },
  {
    label: "Service Areas",
    value: "50+",
    description: "Communities served across three states",
    icon: SparklesIcon,
    image: "/stats/locations.jpg",
  },
];

const testimonials = [
  {
    name: "Sarah W.",
    location: "Boston, MA",
    image: "https://emissaryhealth.org/img/senior2.png",
    text: "Found the perfect caregiver for my mother. The matching process was seamless and the care provided has been exceptional!",
    categories: ["Personal Care", "Mobility Help"],
  },
  {
    name: "Michael T.",
    location: "Manchester, NH",
    image: "https://emissaryhealth.org/img/senior3.jpg",
    text: "The caregivers are incredibly professional and compassionate. They've made such a positive impact on our family's life.",
    categories: ["Companionship", "Memory Care"],
  },
  {
    name: "Emily R.",
    location: "Miami, FL",
    image: "https://emissaryhealth.org/img/senior1.png",
    text: "Exceptional service from start to finish. The care coordinator was helpful and found us the perfect match within days.",
    categories: ["Skilled Nursing", "Medical Care"],
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [error, setError] = useState("");
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showWaitlistPopup, setShowWaitlistPopup] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setShowStickyHeader(scrollPosition > 200); // Adjust this value as needed
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const htmlElement = document.documentElement;
    htmlElement.classList.add("tailwind-html");

    return () => {
      htmlElement.classList.remove("tailwind-html");
    };
  }, []);


  useEffect(() => {
    document.body.classList.add("tailwind-body");

    return () => {
      document.body.classList.remove("tailwind-body");
    };
  }, []);

  const joinWailist = async () => {
    try {
      const response = await axios.post("/auth/waitlist", { email: waitlistEmail, type: selectedType });
      console.log(response.data);
      if (response.data.success) {
        setWaitlistSuccess(true);
        setWaitlistEmail("");
      } else {
        setError("Failed to join the waitlist. Please try again.");
      }
    } catch (error) {
      console.error("Error joining waitlist:", error);
      setError("An error occurred while joining the waitlist. Please try again.");
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!selectedType || !zipCode) {
      setError("Please select your role and enter your zip code");
      return;
    }

    if (!/^\d+$/.test(zipCode.trim())) {
      setError("Please enter a valid zip code");
      return;
    }

    let isInSupportedState = false;

    try {
      const response = await axios.get(`/auth/zipcode/${zipCode}`);
      console.log(response.data);

      if (response.data.success) {
        console.log("Zip code is valid and supported");
        isInSupportedState = true;
      } else {
        console.log("Zip code is not supported");
        setError("Sorry, we are not yet available in your area. Please join our waitlist to get notified when we launch near you.");

      }
    } catch (error) {
      console.error("Error fetching zip code data:", error);
      setError("An error occurred while checking the zip code. Please try again.");
      return; // Stop execution on error
    }

    if (isInSupportedState) {
      if (selectedType === "partner") {
        // navigate("/auth/partner/signup");
        navigate("/auth/caregivers/partner");
      } else if (selectedType === "caregiver") {
        navigate(`/auth/caregivers/caregiver`);
      } else if (selectedType === "individual") {
        navigate("/auth/caregivers/customer");
      }
    } else {
      // navigate("/signup?waitlist=true");
      setShowWaitlistPopup(true);
    }
  };


  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000); // Change testimonial every 5 seconds

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="landing-page">

      {showWaitlistPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 text-center relative transform transition-all duration-300 scale-100">

            <button
              className="absolute top-2 right-3 text-gray-400 hover:text-gray-600 text-xl"
              onClick={() => {
                setShowWaitlistPopup(false);
                setWaitlistSuccess(false);
                setWaitlistEmail('');
              }}
            >
              &times;
            </button>
            {!waitlistSuccess ? (
              <>
                <h2 className="text-xl font-semibold text-sky-700 mb-2">We’re not in your area yet</h2>
                <p className="text-gray-600 mb-4">
                  Currently, we are not working in your state. Leave your email below and we’ll notify you as soon as we launch in your area.
                </p>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    // Send waitlistEmail to your backend here
                    setWaitlistSuccess(true);
                  }}
                >
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-4 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-sky-400"
                    required
                  />
                  <button
                    onClick={joinWailist}
                    type="submit"
                    className="w-full rounded-md bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 font-medium"
                  >
                    Join Now
                  </button>
                </form>
              </>
            ) : (
              <div>
                <h2 className="text-xl font-semibold text-green-600 mb-2">You're on the waitlist!</h2>
                <p className="text-gray-600">
                  We'll keep you posted. Thanks for your interest!
                </p>
              </div>
            )}
          </div>
        </div>
      )}



      <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-sky-50 rounded-3xl md:m-5">
        {/* Sticky Header */}
        <div
          className={`fixed inset-x-0 top-4 z-50 flex justify-center transition-all duration-300 ${showStickyHeader
            ? "translate-y-0 opacity-100"
            : "-translate-y-full opacity-0"
            }`}
        >
          <nav className="w-full max-w-[600px] mx-4 flex items-center justify-between rounded-full bg-white/80 backdrop-blur-md shadow-md shadow-sky-900/5 ring-1 ring-slate-200 px-5 py-4">
            <Link to="/" className="flex items-center space-x-2">
              <img src={logo} alt="Emissary Care" className="h-8 w-auto" />
            </Link>
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-x-8">
              <Link
                to="/about"
                className="text-sm font-semibold leading-6 text-slate-900 hover:text-blue-600 transition-colors duration-200"
              >
                About Us
              </Link>
              <Link
                to="/about/how"
                className="text-sm font-semibold leading-6 text-slate-900 hover:text-blue-600 transition-colors duration-200"
              >
                How It Works
              </Link>
              <div className="flex items-center gap-x-4 pl-8 border-l border-gray-200">
                <Link
                  to="/auth/login"
                  className="text-sm font-semibold leading-6 text-slate-500 hover:text-blue-600 transition-colors duration-200"
                >
                  Log in
                </Link>
                <a
                  href="#get-started"
                  className="group relative rounded-full px-5 py-2 text-sm font-semibold"
                >
                  <span className="absolute inset-0 rounded-full bg-blue-500 transition-all duration-200 ease-in-out group-hover:bg-blue-600"></span>
                  <span className="absolute inset-0 rounded-full ring-2 ring-blue-400/40 transition-all duration-200 ease-in-out group-hover:ring-blue-500/40"></span>
                  <span className="relative text-white">Sign up</span>
                </a>
              </div>
            </div>
            {/* Mobile Menu Button */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center rounded-full bg-slate-50/50 text-slate-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
          </nav>

          {/* Mobile menu overlay */}
          <div
            className={`fixed inset-0 z-50 transition-opacity duration-300 ${isMobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
          >
            <div
              className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm"
              aria-hidden="true"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div
              className={`fixed inset-y-0 inset-x-0 bg-white shadow-2xl transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
                }`}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <Link
                  to="/"
                  className="flex items-center space-x-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <img
                    src={logo}
                    alt="Emissary Care"
                    className="h-8 w-auto"
                  />
                </Link>
                <button
                  type="button"
                  className="rounded-full p-2 text-slate-500 hover:text-blue-600 hover:bg-slate-100 transition-all duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="sr-only">Close menu</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="px-6 py-8 space-y-8 bg-white h-[calc(100vh-72px)] overflow-y-auto">
                <div className="space-y-5">
                  <Link
                    to="/about"
                    className="flex items-center gap-x-2 text-base font-medium text-slate-800 hover:text-blue-600 transition-colors duration-200"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 ring-1 ring-slate-200">
                      <UserGroupIcon className="h-5 w-5 text-slate-600" />
                    </div>
                    About Us
                  </Link>
                  <Link
                    href="/about/how"
                    className="flex items-center gap-x-2 text-base font-medium text-slate-800 hover:text-blue-600 transition-colors duration-200"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 ring-1 ring-slate-200">
                      <SparklesIconOutline className="h-5 w-5 text-slate-600" />
                    </div>
                    How It Works
                  </Link>
                </div>
                <div className="space-y-5 pt-6 border-t border-slate-200">
                  <a
                    href="/auth/login"
                    className="flex items-center gap-x-2 text-base font-medium text-slate-800 hover:text-blue-600 transition-colors duration-200"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 ring-1 ring-slate-200">
                      <UserCircleIconOutline className="h-5 w-5 text-slate-600" />
                    </div>
                    Log in
                  </a>
                  <a
                    href="#get-started"
                    className="flex items-center justify-between w-full rounded-xl bg-blue-500 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-blue-600 transition-colors duration-200 group"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sign up
                    <ChevronRightIcon className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </a>
                </div>
                <div className="pt-6 border-t border-slate-200">
                  <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
                    <p className="text-sm text-slate-600">
                      Now serving Massachusetts, New Hampshire, and Florida
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Regular Header */}
        <header className="relative z-40">
          <nav
            className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8"
            aria-label="Global"
          >
            <div className="flex lg:flex-1">
              <Link to="/" className="flex items-center space-x-2">
                <img
                  src={logo}
                  alt="Emissary Care"
                  className="h-10 w-auto"
                />
              </Link>
            </div>
            <div className="hidden lg:flex lg:flex-1 lg:justify-center">
              <div className="flex gap-x-12">
                <Link
                  to="/about"
                  className="text-sm font-medium leading-6 text-slate-600 hover:text-blue-600 transition-colors duration-200"
                >
                  About Us
                </Link>
                <Link
                  href="/about/how"
                  className="text-sm font-medium leading-6 text-slate-600 hover:text-blue-600 transition-colors duration-200"
                >
                  How It Works
                </Link>
              </div>
            </div>
            <div className="flex lg:flex-1 lg:justify-end">
              <div className="flex items-center gap-x-4">
                <Link
                  to="/auth/login"
                  className="text-sm font-medium leading-6 text-slate-500 hover:text-blue-600 transition-colors duration-200"
                >
                  Log in
                </Link>
                <a
                  href="#get-started"
                  className="group relative rounded-lg px-4 py-2 text-sm font-semibold"
                >
                  <span className="absolute inset-0 rounded-lg bg-blue-500 transition-all duration-200 ease-in-out group-hover:bg-blue-600"></span>
                  <span className="absolute inset-0 rounded-lg ring-2 ring-blue-400/40 transition-all duration-200 ease-in-out group-hover:ring-blue-500/40"></span>
                  <span className="relative text-white">Sign up</span>
                </a>
              </div>
            </div>
          </nav>
        </header>

        {/* Hero section with animated gradient background */}
        <div className="relative isolate">
          {/* Background Grid Pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)] opacity-50" />

          <div
            className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
            aria-hidden="true"
          >
            <div
              className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-sky-300 to-sky-600 opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
              style={{
                clipPath:
                  "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
              }}
            />
          </div>

          <main>
            {/* Hero content */}
            <div className="relative px-6 lg:px-8">
              <div className="mx-auto max-w-7xl py-12 sm:py-20">
                <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
                  <div className="text-center lg:text-left">
                    <div className="mb-8 flex lg:justify-start justify-center">
                      <div className="relative rounded-full px-3 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10 hover:ring-gray-900/20">
                        Now serving Massachusetts, New Hampshire, and Florida{" "}
                        {/* <a
                          href="#locations"
                          className="font-semibold text-sky-600"
                        >
                          <span className="absolute inset-0" aria-hidden="true" />
                          See locations <span aria-hidden="true">&rarr;</span>
                        </a> */}
                      </div>
                    </div>

                    <h1 className="text-4xl font-bold tracking-tight sm:text-6xl leading-tight pb-3 bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                      Find Your <i className="font-serif font-normal">Perfect</i> Caregiver Match Today
                    </h1>


                    <p className="mt-6 text-lg leading-8 text-gray-600">
                      Connect with qualified caregivers or healthcare
                      organizations tailored to your needs
                    </p>
                    <div className="mt-10 flex items-center lg:justify-start justify-center gap-x-6">
                      <a
                        href="#get-started"
                        className="group relative rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 ease-in-out"
                      >
                        <span className="absolute inset-0 rounded-lg bg-blue-500 transition-all duration-200 ease-in-out group-hover:bg-blue-600"></span>
                        <span className="absolute inset-0 rounded-lg ring-2 ring-blue-400/40 transition-all duration-200 ease-in-out group-hover:ring-blue-500/40"></span>
                        <span className="relative flex items-center gap-2">
                          Get started
                          <ChevronRightIcon className="h-4 w-4 transition-transform duration-200 ease-in-out group-hover:translate-x-0.5" />
                        </span>

                      </a>
                      {/* <a
                        href="#learn-more"
                        className="text-sm font-semibold leading-6 text-gray-900 transition-colors duration-200 hover:text-blue-600"
                      >
                        Learn more{" "}
                        <span
                          aria-hidden="true"
                          className="transition-transform duration-200 inline-block hover:translate-x-0.5"
                        >
                          →
                        </span>
                      </a> */}
                    </div>
                  </div>

                  {/* Stacked Testimonials */}
                  <div className="relative mt-12 lg:mt-0">
                    <div className="relative w-full max-w-[540px] mx-auto h-[280px] sm:h-[260px]">
                      {testimonials.map((testimonial, index) => (
                        <div
                          key={index}
                          className={`absolute inset-0 transition-all duration-700 ease-in-out ${index === currentTestimonial
                            ? "opacity-100 translate-y-0 z-20"
                            : index ===
                              (currentTestimonial + 1) % testimonials.length
                              ? "opacity-85 translate-y-10 scale-[0.95] blur-[2px] z-10"
                              : "opacity-0 translate-y-4 z-0"
                            }`}
                        >
                          <div
                            className={`w-full h-full rounded-2xl bg-white/90 backdrop-blur-sm px-8 py-6 shadow-lg shadow-slate-200/50 ring-1 ring-slate-100 flex flex-col ${index === currentTestimonial ? "" : "bg-white/80"
                              }`}
                          >
                            <div className="flex gap-1 mb-1">
                              {[...Array(5)].map((_, i) => (
                                <StarIcon
                                  key={i}
                                  className="h-5 w-5 text-yellow-400"
                                />
                              ))}
                              <span className="ml-1.5 text-sm font-medium text-slate-500">
                                Verified Review
                              </span>
                            </div>
                            <div className="relative mt-4 flex-1">
                              <QuoteIcon className="absolute -top-2 -left-0 h-6 w-6 text-slate-200 transform -scale-x-100" />
                              <p className="pl-8 text-sm sm:text-base font-medium leading-relaxed text-slate-600 text-left">
                                {testimonial.text}
                              </p>
                            </div>
                            <div className="mt-5 flex items-start justify-between gap-x-3 border-t border-slate-100 pt-5">
                              <div className="flex items-center gap-x-3">
                                <div className="relative flex-grow flex-shrink-0">
                                  <img
                                    src={testimonial.image}
                                    alt={testimonial.name}
                                    className="h-12 w-12 rounded-full object-cover ring-2 ring-white"
                                  />
                                  <span className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-sky-50 ring-2 ring-white flex items-center justify-center">
                                    <CheckBadgeIcon
                                      className="h-3.5 w-3.5 text-sky-500"
                                      aria-hidden="true"
                                    />
                                  </span>
                                </div>
                                <div className="text-left flex-grow flex-shrink-0">
                                  <p className="text-sm font-semibold text-slate-800">
                                    {testimonial.name}
                                  </p>
                                  <p className="text-xs text-slate-500 flex items-center gap-x-1">
                                    <MapPinIcon className="h-3.5 w-3.5" />
                                    {testimonial.location}
                                  </p>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2 invisible sm:visible">
                                {testimonial.categories.map((category, i) => (
                                  <span
                                    key={i}
                                    className="inline-flex items-center rounded-full bg-sky-50 px-3 py-1 text-xs font-medium text-sky-500 ring-1 ring-inset ring-sky-100"
                                  >
                                    {category}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Partner Organizations */}
            <div className="mx-auto max-w-7xl px-6 lg:px-8 pt-12">
              <h3 className="text-sm font-medium text-slate-500 text-center mb-8">
                Partner Organizations
              </h3>
              <div className="mx-auto grid grid-cols-4 items-center gap-x-8 gap-y-10 sm:max-w-xl sm:grid-cols-2 sm:gap-x-10 lg:mx-0 lg:max-w-none lg:grid-cols-4">
                <a
                  href="https://cicoa.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="col-span-1 flex justify-center transition-opacity opacity-75 hover:opacity-100"
                >
                  <img
                    className="h-12 object-contain opacity-75"
                    src={cicoaIcon}
                    alt="CICOA"
                  />
                </a>
                <a
                  href="https://mentalhealthmn.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="col-span-1 flex justify-center transition-opacity opacity-75 hover:opacity-100"
                >
                  <img
                    className="h-12 object-contain opacity-75"
                    src={mhmIcon}
                    alt="Mental Health Minnesota"
                  />
                </a>
                <a
                  href="https://www.uhc.com/lp/find-affordable-health-plans"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="col-span-1 flex justify-center transition-opacity opacity-75 hover:opacity-100"
                >
                  <img
                    className="h-12 object-contain opacity-75"
                    src={unitedIcon}
                    alt="United Healthcare"
                  />
                </a>
                <a
                  href="https://www.mass.gov/topics/executive-office-of-health-and-human-services"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="col-span-1 flex justify-center transition-opacity opacity-75 hover:opacity-100"
                >
                  <img
                    className="h-12 object-contain opacity-75"
                    src={massIcon}
                    alt="Massachusetts department of health and human services"
                  />
                </a>
              </div>
            </div>

            {/* Main form section */}
            <div
              id="get-started"
              className="mx-auto max-w-7xl px-6 lg:px-8 pt-24"
            >
              <form
                onSubmit={handleSubmit}
                className="mx-auto max-w-2xl glass-effect rounded-2xl p-8 shadow-slate-200/60 shadow-xl ring-1 ring-gray-200"
              >
                <div className="space-y-12">
                  <div>
                    <h2 className="text-2xl font-bold text-center mb-4 bg-gradient-to-r from-blue-700 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                      Tell us about yourself
                    </h2>
                    <p className="text-gray-500 text-center mb-8">
                      Help us understand your needs so we can match you with the
                      right care solution
                    </p>
                    <div className="mt-10 space-y-10">
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                        {userTypes.map((type) => (
                          <div
                            key={type.id}
                            className={`relative flex cursor-pointer rounded-xl border p-6 shadow-sm focus:outline-none card-hover ${selectedType === type.id
                              ? "border-sky-500 ring-2 ring-sky-500 bg-sky-50"
                              : "border-gray-300 hover:border-sky-400"
                              }`}
                            onClick={() => setSelectedType(type.id)}
                          >
                            <div className="flex w-full flex-col items-center text-center">
                              <div
                                className={`rounded-xl p-2 ${selectedType === type.id
                                  ? "bg-sky-100"
                                  : "bg-gray-50"
                                  }`}
                              >
                                <type.icon
                                  className={`h-8 w-8 ${selectedType === type.id
                                    ? "text-sky-600"
                                    : "text-gray-400"
                                    }`}
                                  aria-hidden="true"
                                />
                              </div>
                              <div className="mt-4">
                                <p
                                  className={`font-semibold ${selectedType === type.id
                                    ? "text-sky-900"
                                    : "text-gray-900"
                                    }`}
                                >
                                  {type.title}
                                </p>
                                <p
                                  className={`mt-1 text-sm ${selectedType === type.id
                                    ? "text-sky-700"
                                    : "text-gray-500"
                                    }`}
                                >
                                  {type.description}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="sm:col-span-2">
                        <label
                          htmlFor="zipCode"
                          className="block text-sm font-medium leading-6 text-gray-900"
                        >
                          Enter your ZIP code
                        </label>
                        <div className="mt-2">
                          <div className="relative rounded-md shadow-sm">
                            <input
                              type="text"
                              name="zipCode"
                              id="zipCode"
                              value={zipCode}
                              onChange={(e) => setZipCode(e.target.value)}
                              className="input-primary pl-4 pr-12 text-black zipcode-input"
                              placeholder="e.g. 02108"
                            />
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                              <ChevronRightIcon
                                className="h-5 w-5 text-gray-400"
                                aria-hidden="true"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="mt-4 text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg">
                    {error}
                  </div>
                )}

                <div className="mt-8 flex items-center justify-center">
                  <button
                    type="submit"
                    className="group relative rounded-lg px-8 py-3 text-base font-semibold text-white transition-all duration-200 ease-in-out"
                  >
                    <span className="absolute inset-0 rounded-lg bg-blue-500 transition-all duration-200 ease-in-out group-hover:bg-blue-600"></span>
                    <span className="absolute inset-0 rounded-lg ring-2 ring-blue-400/40 transition-all duration-200 ease-in-out group-hover:ring-blue-500/40"></span>
                    <span className="relative flex items-center gap-2">
                      Continue
                      <ChevronRightIcon className="h-5 w-5 transition-transform duration-200 ease-in-out group-hover:translate-x-0.5" />
                    </span>
                  </button>
                </div>
              </form>
            </div>

            {/* Services section with feature lists */}
            <div className="mx-auto mt-32 max-w-7xl px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white py-12 rounded-t-3xl">
              <div className="mx-auto max-w-2xl lg:text-center">
                <div className="mb-8 flex justify-center">
                  <div className="relative rounded-full px-3 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10 hover:ring-gray-900/20">
                    <SparklesIconOutline className="inline-block h-4 w-4 mr-1" />
                    Comprehensive Care
                  </div>
                </div>
                <p className="mt-2 text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent sm:text-4xl">
                  Our Services
                </p>
                <p className="mt-6 text-lg leading-8 text-gray-600">
                  We offer a wide range of care services tailored to meet your
                  specific needs and preferences
                </p>
              </div>
              <div className="mx-auto mt-16 max-w-2xl lg:max-w-none">
                <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
                  {services.map((service) => (
                    <div
                      key={service.title}
                      className="relative flex flex-col rounded-3xl bg-white/5 p-10 shadow-xl shadow-slate-900/10 ring-1 ring-gray-200 transition-all duration-300 hover:shadow-2xl hover:shadow-sky-900/10 hover:-translate-y-1 overflow-hidden group"
                      style={{
                        backgroundImage: `url(${service.backgroundImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    >
                      {/* Dark gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/50 to-slate-900/30 transition-opacity group-hover:opacity-75 rounded-3xl overflow-hidden" />

                      {/* Content with backdrop blur */}
                      <div className="relative z-10 overflow-hidden">
                        <dt className="flex flex-col gap-y-4">
                          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm shadow-lg ring-1 ring-white/20">
                            <service.icon
                              className="h-10 w-10 text-white/80"
                              aria-hidden="true"
                            />
                          </div>
                          <div className="text-xl font-semibold leading-7 text-white">
                            {service.title}
                          </div>
                        </dt>
                        <dd className="mt-4 flex flex-auto flex-col">
                          <p className="flex-auto text-base leading-7 text-slate-200">
                            {service.description}
                          </p>
                          <div className="mt-8">
                            <div className="relative">
                              <div
                                className="absolute inset-0 flex items-center"
                                aria-hidden="true"
                              >
                                <div className="w-full border-t border-white/10" />
                              </div>
                              <div className="relative flex justify-start">
                                <span className="bg-white/10 backdrop-blur-md px-3 py-1 text-sm font-semibold text-white rounded-full">
                                  Features
                                </span>
                              </div>
                            </div>
                            <ul role="list" className="mt-4 space-y-4">
                              {service.features.map((feature) => (
                                <li
                                  key={feature}
                                  className="flex items-center gap-x-3"
                                >
                                  <div className="flex-shrink-0">
                                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 backdrop-blur-md shadow-md">
                                      <SparklesIconOutline
                                        className="h-4 w-4 text-sky-600"
                                        aria-hidden="true"
                                      />
                                    </div>
                                  </div>
                                  <span className="text-sm text-slate-100 font-medium">
                                    {feature}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </dd>
                      </div>
                      <div className="absolute right-6 top-6 z-10">
                        <div className="flex h-8 items-center rounded-full bg-white/90 backdrop-blur-md px-3 text-xs font-medium text-slate-800 shadow-lg">
                          Available 24/7
                        </div>
                      </div>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            {/* Trust indicators */}
            <div className="relative isolate px-6 lg:px-8 py-12">
              {/* Background Grid Pattern */}

              <div className="mx-auto max-w-7xl">
                <div className="mx-auto max-w-2xl lg:text-center">
                  <div className="mb-8 flex justify-center">
                    <div className="relative rounded-full px-3 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10 hover:ring-gray-900/20">
                      <SparklesIconOutline className="inline-block h-4 w-4 mr-1" />
                      Why Choose Emissary Care
                    </div>
                  </div>
                  <h2 className="text-4xl font-bold tracking-tight pb-1 sm:text-5xl bg-gradient-to-r from-blue-500 via-sky-500 to-sky-500 bg-clip-text text-transparent">
                    Trusted by Families{" "}
                    <i className="font-serif font-normal">Nationwide</i>
                  </h2>
                  <p className="mt-6 text-lg leading-8 text-gray-600">
                    Join thousands of families who have found their perfect care
                    solution through our platform
                  </p>
                </div>

                <div className="mx-auto mt-16 grid max-w-lg grid-cols-1 items-center gap-x-12 gap-y-16 sm:max-w-xl sm:grid-cols-2 sm:gap-x-10 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                  {/* Licensed & Insured Card */}
                  <div className="relative group">
                    <div className="absolute -inset-4 rounded-2xl bg-gradient-to-r from-sky-50 to-sky-100/50 opacity-0 transition duration-500 group-hover:opacity-100" />
                    <div className="relative flex flex-col items-center p-8 rounded-2xl bg-white/80 backdrop-blur-sm shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 transition-all duration-300 hover:shadow-xl hover:shadow-sky-900/10">
                      <div className="absolute -top-8">
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/50 backdrop-blur-md ring-1 ring-slate-100/80 shadow-lg shadow-slate-500/5">
                          <ShieldCheckIconOutline className="h-8 w-8 text-sky-400" />
                        </div>
                      </div>
                      <h3 className="mt-8 text-lg font-semibold text-slate-900">
                        Licensed & Insured
                      </h3>
                      <p className="mt-2 text-center text-sm leading-6 text-slate-600">
                        All our caregivers are fully licensed, bonded, and
                        insured. We maintain strict compliance with state
                        regulations.
                      </p>
                      <ul className="mt-6 space-y-4 border-t border-slate-100 pt-6 w-full">
                        <li className="flex items-center gap-x-3">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-b from-sky-50 to-sky-100 ring-1 ring-sky-100">
                            <CheckBadgeIcon className="h-4 w-4 flex-none text-sky-600" />
                          </div>
                          <span className="text-sm text-slate-600 font-medium">
                            Background verified
                          </span>
                        </li>
                        <li className="flex items-center gap-x-3">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-b from-sky-50 to-sky-100 ring-1 ring-sky-100">
                            <CheckBadgeIcon className="h-4 w-4 flex-none text-sky-600" />
                          </div>
                          <span className="text-sm text-slate-600 font-medium">
                            Professional certifications
                          </span>
                        </li>
                        <li className="flex items-center gap-x-3">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-b from-sky-50 to-sky-100 ring-1 ring-sky-100">
                            <CheckBadgeIcon className="h-4 w-4 flex-none text-sky-600" />
                          </div>
                          <span className="text-sm text-slate-600 font-medium">
                            Liability coverage
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Compassionate Care Card */}
                  <div className="relative group">
                    <div className="absolute -inset-4 rounded-2xl bg-gradient-to-r from-sky-50 to-sky-100/50 opacity-0 transition duration-500 group-hover:opacity-100" />
                    <div className="relative flex flex-col items-center p-8 rounded-2xl bg-white/80 backdrop-blur-sm shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 transition-all duration-300 hover:shadow-xl hover:shadow-sky-900/10">
                      <div className="absolute -top-8">
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/50 backdrop-blur-md ring-1 ring-slate-100/80 shadow-lg shadow-slate-500/5">
                          <HeartIconOutline className="h-8 w-8 text-sky-400" />
                        </div>
                      </div>
                      <h3 className="mt-8 text-lg font-semibold text-slate-900">
                        Compassionate Care
                      </h3>
                      <p className="mt-2 text-center text-sm leading-6 text-slate-600">
                        Our caregivers are selected for their empathy and
                        dedication to providing exceptional care services.
                      </p>
                      <ul className="mt-6 space-y-4 border-t border-slate-100 pt-6 w-full">
                        <li className="flex items-center gap-x-3">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-b from-sky-50 to-sky-100 ring-1 ring-sky-100">
                            <CheckBadgeIcon className="h-4 w-4 flex-none text-sky-600" />
                          </div>
                          <span className="text-sm text-slate-600 font-medium">
                            Empathy-focused hiring
                          </span>
                        </li>
                        <li className="flex items-center gap-x-3">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-b from-sky-50 to-sky-100 ring-1 ring-sky-100">
                            <CheckBadgeIcon className="h-4 w-4 flex-none text-sky-600" />
                          </div>
                          <span className="text-sm text-slate-600 font-medium">
                            Ongoing training
                          </span>
                        </li>
                        <li className="flex items-center gap-x-3">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-b from-sky-50 to-sky-100 ring-1 ring-sky-100">
                            <CheckBadgeIcon className="h-4 w-4 flex-none text-sky-600" />
                          </div>
                          <span className="text-sm text-slate-600 font-medium">
                            Cultural sensitivity
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Quality Assured Card */}
                  <div className="relative group">
                    <div className="absolute -inset-4 rounded-2xl bg-gradient-to-r from-sky-50 to-sky-100/50 opacity-0 transition duration-500 group-hover:opacity-100" />
                    <div className="relative flex flex-col items-center p-8 rounded-2xl bg-white/80 backdrop-blur-sm shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 transition-all duration-300 hover:shadow-xl hover:shadow-sky-900/10">
                      <div className="absolute -top-8">
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/50 backdrop-blur-md ring-1 ring-slate-100/80 shadow-lg shadow-slate-500/5">
                          <SparklesIconOutline className="h-8 w-8 text-sky-400" />
                        </div>
                      </div>
                      <h3 className="mt-8 text-lg font-semibold text-slate-900">
                        Quality Assured
                      </h3>
                      <p className="mt-2 text-center text-sm leading-6 text-slate-600">
                        We maintain high standards through rigorous screening and
                        continuous quality monitoring.
                      </p>
                      <ul className="mt-6 space-y-4 border-t border-slate-100 pt-6 w-full">
                        <li className="flex items-center gap-x-3">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-b from-sky-50 to-sky-100 ring-1 ring-sky-100">
                            <CheckBadgeIcon className="h-4 w-4 flex-none text-sky-600" />
                          </div>
                          <span className="text-sm text-slate-600 font-medium">
                            Regular assessments
                          </span>
                        </li>
                        <li className="flex items-center gap-x-3">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-b from-sky-50 to-sky-100 ring-1 ring-sky-100">
                            <CheckBadgeIcon className="h-4 w-4 flex-none text-sky-600" />
                          </div>
                          <span className="text-sm text-slate-600 font-medium">
                            Client feedback system
                          </span>
                        </li>
                        <li className="flex items-center gap-x-3">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-b from-sky-50 to-sky-100 ring-1 ring-sky-100">
                            <CheckBadgeIcon className="h-4 w-4 flex-none text-sky-600" />
                          </div>
                          <span className="text-sm text-slate-600 font-medium">
                            Performance tracking
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
